import { useEffect, useMemo, useState } from "react";
import type { CaptureAnswerValue, CaptureAnswers, CaptureField, CaptureFormContract, CapturePresentationMode, CaptureRequest } from "../../types";
import { CaptureFieldControl, type CaptureAttachment } from "./CaptureFieldControl";
import { useCaptureWorkspaceRecoveryUI } from "./CaptureWorkspaceRecoveryContext";
import { answerIsPresent, answerText, documentAlreadyReceived, effectivePresentationMode, normalizeFieldType, validateCaptureFields, visibleCaptureSections } from "./contract";
import { CaptureFieldPreviousResponseNotice, CaptureFieldSourceNotice } from "./sourceProvenance";

type Props = {
  request?: CaptureRequest;
  contract: CaptureFormContract;
  answers: CaptureAnswers;
  attachments: Record<string, CaptureAttachment[]>;
  mode: CapturePresentationMode;
  external: boolean;
  fullPage?: boolean;
  uploadingField: string | null;
  onAnswer: (fieldID: string, value: CaptureAnswerValue) => void;
  onUpload: (field: CaptureField, files: File[], previewURL?: string) => void;
  onRemoveAttachment: (field: CaptureField, attachmentID: string) => void;
  onModeChange: (mode: "CLASSIC" | "WIZARD") => void;
  onBeforeSectionNavigation?: () => Promise<boolean> | boolean;
  onReview: () => void;
};

export function CaptureForm({ request, contract, answers, attachments, mode, external, fullPage = false, uploadingField, onAnswer, onUpload, onRemoveAttachment, onModeChange, onBeforeSectionNavigation, onReview }: Props) {
  const sections = visibleCaptureSections(contract, answers);
  const effectiveMode = effectivePresentationMode(contract, answers, mode);
  const recoveryUI = useCaptureWorkspaceRecoveryUI();
  const initialPage = clampSectionIndex(recoveryUI?.initialPage ?? 0, sections.length);
  const [sectionIndex, setSectionIndex] = useState(initialPage);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [navigating, setNavigating] = useState(false);
  const unsupported = contract.fields.filter((field) => !normalizeFieldType(field.type));
  const reselectFields = useMemo(() => new Set(recoveryUI?.filesToReselect ?? []), [recoveryUI?.filesToReselect]);

  useEffect(() => {
    setSectionIndex(clampSectionIndex(recoveryUI?.initialPage ?? 0, sections.length));
  }, [recoveryUI?.initialPage, sections.length]);

  function update(fieldID: string, value: CaptureAnswerValue) {
    setErrors((current) => {
      if (!current[fieldID]) return current;
      const next = { ...current };
      delete next[fieldID];
      return next;
    });
    onAnswer(fieldID, value);
  }

  function validate(fields: CaptureField[], next: () => void) {
    const failures = validateCaptureFields(fields, answers);
    setErrors(Object.fromEntries(failures.map((failure) => [failure.fieldID, failure.message])));
    if (failures.length === 0) next();
  }

  function moveToSection(nextIndex: number) {
    const next = clampSectionIndex(nextIndex, sections.length);
    setErrors({});
    setSectionIndex(next);
    recoveryUI?.onPageChange(next);
  }

  function navigateToSection(nextIndex: number) {
    if (!onBeforeSectionNavigation) {
      moveToSection(nextIndex);
      return;
    }
    setNavigating(true);
    void Promise.resolve(onBeforeSectionNavigation()).then((saved) => {
      if (saved) moveToSection(nextIndex);
    }).finally(() => setNavigating(false));
  }

  function renderFields(fields: CaptureField[]) {
    return fields.map((field) => <div className="capture-field-shell" id={`capture-field-${field.id}`} key={field.id}><CaptureFieldControl
      field={field}
      value={answers[field.id]}
      attachments={attachments[field.id]}
      uploading={uploadingField === field.id}
      external={external}
      error={errors[field.id]}
      onChange={(value) => update(field.id, value)}
      onUpload={(files, previewURL) => onUpload(field, files, previewURL)}
      onRemove={(attachmentID) => onRemoveAttachment(field, attachmentID)}
    />{reselectFields.has(field.id) && !documentAlreadyReceived(field) && <p className="field-help" role="status">Reselect file to upload</p>}<CaptureFieldSourceNotice field={field} value={answerText(answers[field.id])}/>{request && <CaptureFieldPreviousResponseNotice request={request} field={field} value={answerText(answers[field.id])}/>}</div>);
  }

  const errorEntries = Object.entries(errors);
  const modeSwitch = contract.presentation.allow_mode_switch && <div className="capture-mode-switch" role="group" aria-label="Question layout"><button type="button" className="secondary-button" aria-pressed={effectiveMode === "CLASSIC"} onClick={() => onModeChange("CLASSIC")}>Show all questions</button><button type="button" className="secondary-button" aria-pressed={effectiveMode === "WIZARD"} onClick={() => onModeChange("WIZARD")}>Show one section at a time</button></div>;
  const errorSummary = errorEntries.length > 0 && <div className="capture-error-summary" role="alert" aria-labelledby="capture-error-summary-title"><strong id="capture-error-summary-title">Check the highlighted answers</strong><ul>{errorEntries.map(([fieldID, message]) => <li key={fieldID}><a href={`#capture-field-${fieldID}`}>{message}</a></li>)}</ul></div>;
  const reviewLabel = external ? "Review and submit" : "Review response";

  if (sections.length === 0) return <div className="capture-form"><p>No questions are required for the answers provided.</p><div className="wizard-actions"><button className="primary-button" type="button" onClick={onReview}>{reviewLabel}</button></div></div>;

  if (effectiveMode === "CLASSIC") {
    const fields = sections.flatMap((section) => section.fields);
    return <div className="capture-form">{modeSwitch}{sections.length > 1 && <nav className="capture-section-index" aria-label="Request sections"><span>Sections</span><ol>{sections.map((section) => <li key={section.id}><a href={`#capture-section-${section.id}`}>{section.title}</a></li>)}</ol></nav>}{errorSummary}{unsupported.length > 0 && <UnsupportedFields fields={unsupported}/>} {sections.map((section) => <section className="capture-section" id={`capture-section-${section.id}`} aria-labelledby={`capture-section-title-${section.id}`} key={section.id}><h3 id={`capture-section-title-${section.id}`}>{section.title}</h3>{section.help && <p className="field-help">{section.help}</p>}{renderFields(section.fields)}</section>)}<div className="wizard-actions"><button className="primary-button" type="button" disabled={unsupported.length > 0 || Boolean(uploadingField)} onClick={() => validate(fields, onReview)}>{uploadingField ? "Uploading…" : reviewLabel}</button></div></div>;
  }

  const section = sections[sectionIndex] ?? sections[0]!;
  const isLast = sectionIndex === sections.length - 1;
  // Readiness is based only on currently visible questionnaire fields and
  // already-received documents; neither presence nor upload implies approval.
  const visibleFields = sections.flatMap((entry) => entry.fields);
  const hasValue = (field: CaptureField) => {
    if (documentAlreadyReceived(field)) return true;
    const value = answers[field.id];
    const type = normalizeFieldType(field.type);
    if (type === "file" || type === "photo" || type === "vendor_document") {
      return Boolean(value?.artifact_ids?.length || value?.document?.artifact_id);
    }
    return answerIsPresent(value);
  };
  const answeredCount = visibleFields.filter(hasValue).length;
  const missingRequiredCount = visibleFields.filter((field) => field.required && !hasValue(field)).length;
  const evidenceFields = visibleFields.filter((field) =>
    ["file", "photo", "vendor_document"].includes(normalizeFieldType(field.type) ?? ""));
  const attachedCount = evidenceFields.filter(hasValue).length;
  const receivedFileNames = (field: CaptureField): string[] => {
    const answer = answers[field.id];
    const selected = new Set([...(answer?.artifact_ids ?? []), answer?.document?.artifact_id].filter((id): id is string => Boolean(id)));
    return (attachments[field.id] ?? [])
      .filter((file) => file.id && selected.has(file.id) && file.file_name.trim())
      .map((file) => file.file_name);
  };
  return <div className={fullPage ? "capture-form capture-wizard capture-wizard--full-page" : "capture-form capture-wizard"}>
    {fullPage && <nav className="capture-wizard__views" aria-label="Assessment views">
      <span aria-current="page">Questions</span>
      <button type="button" disabled title="Reviewed Excel-answer import is unavailable">Excel import</button>
      <button type="button" disabled={Boolean(uploadingField) || unsupported.length > 0}
        onClick={() => validate(visibleFields, onReview)}>Review</button>
    </nav>}
    {fullPage && <section className="capture-wizard__completion" aria-label="Assessment completion">
      <div><span>Completion</span><strong>{answeredCount} of {visibleFields.length} answered</strong></div>
      <progress max={Math.max(visibleFields.length, 1)} value={answeredCount} aria-label="Questions answered"/>
      <strong>{visibleFields.length ? Math.round(answeredCount * 100 / visibleFields.length) : 0}%</strong>
      <small>{missingRequiredCount} required unanswered · {attachedCount} documents received</small>
    </section>}
    {modeSwitch}
    <div className={sections.length > 1 ? "capture-wizard__layout capture-wizard__layout--sectioned" : "capture-wizard__layout"}>
      {sections.length > 1 && <nav className="capture-wizard__sections" aria-label="Assessment sections">
        <strong>Sections</strong>
        <ol>{sections.map((entry, index) => <li key={entry.id}>
          <button type="button" aria-label={fullPage ? `${entry.title}, ${entry.fields.filter(hasValue).length} of ${entry.fields.length} answered` : entry.title}
            aria-current={index === sectionIndex ? "step" : undefined}
            disabled={navigating || Boolean(uploadingField) || (index > sectionIndex && unsupported.length > 0) || index > sectionIndex + 1}
            onClick={() => {
              if (index === sectionIndex) return;
              if (index < sectionIndex) navigateToSection(index);
              else validate(section.fields, () => navigateToSection(index));
            }}>
            <span className="capture-wizard__section-number" aria-hidden="true">{index + 1}</span>
            <span>{entry.title}</span>
            {fullPage && <small className="capture-wizard__section-count" aria-hidden="true">
              {entry.fields.filter(hasValue).length}/{entry.fields.length}
            </small>}
          </button>
        </li>)}</ol>
      </nav>}
      <div className="capture-wizard__main">
        <div className="capture-progress"><span aria-live="polite">{fullPage ? "Section " : "Step "}{sectionIndex + 1} of {sections.length}</span>{!fullPage && <progress max={sections.length} value={sectionIndex + 1} aria-label={"Section " + (sectionIndex + 1) + " of " + sections.length}/>}</div>
        {errorSummary}
        {unsupported.length > 0 && <UnsupportedFields fields={unsupported}/>}
        <section className="capture-section" aria-labelledby={"capture-section-title-" + section.id}><h3 id={"capture-section-title-" + section.id}>{section.title}</h3>{section.help && <p className="field-help">{section.help}</p>}{renderFields(section.fields)}</section>
        <div className="wizard-actions">
          {sectionIndex > 0 && <button className="secondary-button" type="button" disabled={navigating} onClick={() => navigateToSection(sectionIndex - 1)}>Back</button>}
          <button className="primary-button" type="button" disabled={unsupported.length > 0 || Boolean(uploadingField) || navigating} onClick={() => validate(section.fields, () => isLast ? onReview() : navigateToSection(sectionIndex + 1))}>{uploadingField ? "Uploading…" : navigating ? "Saving…" : isLast ? reviewLabel : "Continue"}</button>
        </div>
      </div>
      <aside className="capture-wizard__readiness" aria-label="Response readiness and evidence">
        <section className="capture-wizard__summary" aria-labelledby="capture-readiness-heading">
          <h3 id="capture-readiness-heading">Review readiness</h3>
          <dl>
            <div><dt>Answers recorded</dt><dd>{answeredCount} / {visibleFields.length}</dd></div>
            <div><dt>Required unanswered</dt><dd>{missingRequiredCount}</dd></div>
            <div><dt>Evidence received</dt><dd>{attachedCount} / {evidenceFields.length}</dd></div>
          </dl>
        </section>
        {evidenceFields.length > 0 && <section className="capture-wizard__evidence" aria-labelledby="capture-evidence-heading">
          <h3 id="capture-evidence-heading">Evidence</h3>
          <ul>{evidenceFields.map((field) => {
            const files = receivedFileNames(field);
            return <li key={field.id}>
              <span>{field.label}</span>
              <span>{hasValue(field) ? "Received" : field.required ? "Required" : "Not attached"}</span>
              {files.length > 0 && <small className="capture-wizard__evidence-files">
                {files.slice(0, 3).join(" · ")}{files.length > 3 ? ` · +${files.length - 3} more` : ""}
              </small>}
            </li>;
          })}</ul>
        </section>}
      </aside>
    </div>
  </div>;
}

function UnsupportedFields({ fields }: { fields: CaptureField[] }) {
  return <div className="inline-error" role="alert"><strong>This request includes a field that cannot be collected here.</strong><p>{fields.map((field) => field.label).join(", ")}. Ask the sender to update the request.</p></div>;
}

function clampSectionIndex(value: number, sectionCount: number) {
  return Math.min(Math.max(0, Math.trunc(value)), Math.max(sectionCount - 1, 0));
}
